import { useState } from 'react'
import constate from 'constate'

const EMPTY_SELECTED_ITEMS = []

export function useSelectItems() {
  const [selectedItems, setSelectedItems] = useState(EMPTY_SELECTED_ITEMS)
  const [selectedAllItemsOnCurrentPage, setSelectedAllItemsOnCurrentPage] = useState(false)
  const [selectedAllItemsOnAllPages, setSelectedAllItemsOnAllPages] = useState(false)
  const [queryParams, setQueryParams] = useState({})

  const isItemSelected = currentItem =>
    selectedAllItemsOnAllPages || selectedItems.some(item => item.id === currentItem.id)

  const hasSelectedItemsOnCurrentPage = items =>
    items && selectedItems.some(selectedItem => items.some(item => item.id === selectedItem.id))

  const selectItems = (currentItems, allItems) => {
    const newSelectedItems = [...selectedItems, ...currentItems]
    setSelectedItems(newSelectedItems)

    if (allItems && allItems.length === newSelectedItems.length) {
      setSelectedAllItemsOnCurrentPage(true)
    }
  }

  const deselectItems = currentItems => {
    const idsToRemove = currentItems.map(ci => ci.id)
    setSelectedItems(selectedItems.filter(item => !idsToRemove.includes(item.id)))
    setSelectedAllItemsOnCurrentPage(false)
    setSelectedAllItemsOnAllPages(false)
  }

  const toggleItem = (currentItem, allItems) => {
    const isSelected = isItemSelected(currentItem)

    if (isSelected) {
      setSelectedItems(selectedItems.filter(item => item.id !== currentItem.id))
      setSelectedAllItemsOnCurrentPage(false)
      setSelectedAllItemsOnAllPages(false)
    } else {
      const newSelectedItems = [...selectedItems, currentItem]
      setSelectedItems(newSelectedItems)

      // Should this be `areAllItemsSelectedOnCurrentPage(allItems)`?
      if (allItems && allItems.length === newSelectedItems.length) {
        setSelectedAllItemsOnCurrentPage(true)
      }
    }
  }

  const toggleAllItemsOnCurrentPage = items => {
    if (selectedAllItemsOnCurrentPage) {
      setSelectedItems(selectedItems.filter(selectedItem => !items.some(item => item.id === selectedItem.id)))
    } else {
      setSelectedItems([
        ...selectedItems,
        ...items.filter(item => !selectedItems.some(selectedItem => selectedItem.id === item.id))
      ])
    }

    if (selectedAllItemsOnCurrentPage) {
      setSelectedAllItemsOnAllPages(false)
    }

    setSelectedAllItemsOnCurrentPage(!selectedAllItemsOnCurrentPage)
  }

  const toggleAllItemsOnAllPages = items => {
    setSelectedItems([
      ...selectedItems,
      ...items.filter(item => !selectedItems.some(selectedItem => selectedItem.id === item.id))
    ])

    if (!selectedAllItemsOnCurrentPage) {
      setSelectedAllItemsOnCurrentPage(true)
    }

    if (selectedAllItemsOnAllPages) {
      setSelectedAllItemsOnCurrentPage(false)
    }

    setSelectedAllItemsOnAllPages(!selectedAllItemsOnAllPages)
  }

  const areAllItemsSelectedOnCurrentPage = currentPageItems => {
    const selectedIds = selectedItems.map(si => si.id)
    return currentPageItems.length > 0 && currentPageItems.every(item => selectedIds.includes(item.id))
  }

  const areAllItemsSelectedOnAllPages = items => {
    if (items.length > selectedItems.length) {
      return false
    }
    const selectedIds = selectedItems.map(si => si.id)
    return items.length > 0 && items.every(item => selectedIds.includes(item.id))
  }

  const handleItemsChange = items => {
    const areAllItemsSelected = areAllItemsSelectedOnCurrentPage(items)
    if (areAllItemsSelected) {
      setSelectedAllItemsOnCurrentPage(true)
    } else if (selectedAllItemsOnAllPages) {
      setSelectedItems([
        ...selectedItems,
        ...items.filter(item => !selectedItems.some(selectedItem => selectedItem.id === item.id))
      ])
      setSelectedAllItemsOnCurrentPage(true)
    } else {
      setSelectedAllItemsOnCurrentPage(false)
    }
  }

  const deselectAllItems = () => {
    setSelectedItems(EMPTY_SELECTED_ITEMS)
    setSelectedAllItemsOnCurrentPage(false)
    setSelectedAllItemsOnAllPages(false)
  }

  const replaceSelectedItems = (currentItems, allItems) => {
    setSelectedItems(currentItems)
    setSelectedAllItemsOnAllPages(false)
    if (allItems && allItems.length === currentItems.length && currentItems.length > 0) {
      setSelectedAllItemsOnCurrentPage(true)
    } else {
      setSelectedAllItemsOnCurrentPage(false)
    }
  }

  const unsetSelectedAllItemsOnAllPages = () => {
    setSelectedAllItemsOnAllPages(false)
  }

  const recalculateSelectedAllFlags = (currPageItems, allPageItems) => {
    const allItemsSelected = areAllItemsSelectedOnAllPages(allPageItems)
    const allCurrPageItemsSelected = allItemsSelected || areAllItemsSelectedOnCurrentPage(currPageItems)

    setSelectedAllItemsOnCurrentPage(allCurrPageItemsSelected)
    setSelectedAllItemsOnAllPages(allItemsSelected)
  }

  return {
    selectedItems,
    selectedAllItemsOnCurrentPage,
    selectedAllItemsOnAllPages,
    isItemSelected,
    hasSelectedItemsOnCurrentPage,
    toggleItem,
    toggleAllItemsOnCurrentPage,
    toggleAllItemsOnAllPages,
    handleItemsChange,
    deselectAllItems,
    unsetSelectedAllItemsOnAllPages,
    queryParams,
    setQueryParams,
    selectItems,
    deselectItems,
    replaceSelectedItems,
    recalculateSelectedAllFlags
  }
}

const [SelectItemsProvider, useSelectItemsContext] = constate(useSelectItems)
export { SelectItemsProvider, useSelectItemsContext }
